import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  answers: [],
  percentage: 0,
  currentGroupIndex: 0,
  videosSeen: {},
  quizMode: "traditional", // 'traditional' | 'chatbot' | 'anam'
  isStarted: false,
  introVideoSeen: false,
  showQuestions: false,
  alertQuestionIds: [],
  error: null,
  termsAccepted: false,
  isAllquestionsAnswered: false,
  isSessionClosed: false,
  isSubmitting: false,
  uploadingFiles: {},
  uploadedFileUrls: {},
  fileErrors: {},
  messages: [],
  sessionId: null,
};

export const quizSlice = createSlice({
  name: "quiz",
  initialState,
  reducers: {
    startQuiz: (state) => {
      state.isStarted = true;
    },
    setQuizMode: (state, { payload }) => {
      state.quizMode = payload;
      state.answers = [];
      state.messages = [];
      state.percentage = 0;
      state.sessionId = null;
      state.termsAccepted = false;
      state.isAllquestionsAnswered = false;
      state.isSessionClosed = false;
    },
    updateAnswer: (state, { payload }) => {
      const { questionId, value } = payload;
      const existingAnswerIndex = state.answers.findIndex(
        (answer) => answer.questionId === questionId
      );

      if (existingAnswerIndex !== -1) {
        state.answers[existingAnswerIndex].value = value;
      } else {
        state.answers.push({ questionId, value });
      }
    },
    removeAnswer: (state, { payload }) => {
      const { questionId } = payload;
      state.answers = state.answers.filter(
        (answer) => answer.questionId !== questionId
      );
    },
    updatePercentage: (state, { payload }) => {
      state.percentage = payload;
    },
    setCurrentGroupIndex: (state, { payload }) => {
      state.currentGroupIndex = payload;
    },
    markVideoAsSeen: (state, { payload }) => {
      const { groupIndex, seen = true } = payload;
      state.videosSeen[groupIndex] = seen;
    },
    nextGroup: (state) => {
      state.currentGroupIndex += 1;
    },
    prevGroup: (state) => {
      if (state.currentGroupIndex > 0) {
        state.currentGroupIndex -= 1;
      }
    },
    resetQuiz: (state) => {
      return { ...initialState };
    },
    clearAnswers: (state) => {
      state.answers = [];
      state.percentage = 0;
      state.sessionId = null;
      state.messages = [];
    },
    setIntroVideoSeen: (state, { payload }) => {
      state.introVideoSeen = payload;
      if (payload) state.showQuestions = true;
    },
    setAlertQuestionIds: (state, { payload }) => {
      state.alertQuestionIds = payload;
    },
    setError: (state, { payload }) => {
      state.error = payload;
    },
    removeQuestionAlert: (state, { payload }) => {
      state.alertQuestionIds = state.alertQuestionIds.filter(
        (id) => id !== payload
      );
    },
    goBackToStart: (state) => {
      state.isStarted = false;
      state.introVideoSeen = false;
      state.showQuestions = false;
      state.alertQuestionIds = [];
      state.error = null;
      state.answers = [];
      state.percentage = 0;
      state.termsAccepted = false;
      state.isAllquestionsAnswered = false;
      state.isSessionClosed = false;
    },
    setTermsAccepted: (state, { payload }) => {
      state.termsAccepted = payload;
    },
    setSubmitting: (state, { payload }) => {
      state.isSubmitting = payload;
    },

    // files
    setUploadingFile: (state, { payload }) => {
      const { questionId, uploading } = payload;
      state.uploadingFiles[questionId] = state.uploadingFiles[questionId] ?? {};
      state.uploadingFiles[questionId].isLoading = uploading;
      state.uploadingFiles[questionId].message = null;
    },
    updateAnswerFile: (state, { payload }) => {
      const { questionId, files, message } = payload;

      const existingAnswerIndex = state.answers.findIndex(
        (answer) => answer.questionId === questionId
      );

      if (existingAnswerIndex !== -1) {
        state.answers[existingAnswerIndex].files = files;
      } else {
        state.answers.push({ questionId, value: "", files });
      }

      state.uploadingFiles[questionId].isLoading = false;
      state.uploadingFiles[questionId].message = {
        type: "success",
        text: message,
      };
      state.uploadingFiles[questionId].files = files;
    },
    setFileError: (state, { payload }) => {
      const { questionId, message } = payload;
      state.uploadingFiles[questionId].isLoading = false;
      state.uploadingFiles[questionId].message = {
        type: "error",
        text: message,
      };
    },
    removeUploadedFile: (state, { payload }) => {
      const { questionId } = payload;

      const existingAnswerIndex = state.answers.findIndex(
        (answer) => answer.questionId === questionId
      );
      if (existingAnswerIndex !== -1) {
        delete state.answers[existingAnswerIndex].files;
      }

      state.uploadingFiles[questionId].isLoading = false;
      state.uploadingFiles[questionId].message = null;
      state.uploadingFiles[questionId].files = null;
    },
    clearFileError: (state, { payload }) => {
      const { questionId } = payload;
      delete state.uploadingFiles[questionId].message;
    },
    setUploadedFileUrl: (state, { payload }) => {
      const { questionId, url, fileName } = payload;
      state.uploadedFileUrls[questionId] = { url, fileName };
      state.uploadingFiles[questionId] = false;
    },

    setAllQuestionsAnswered: (state, { payload }) => {
      state.isAllquestionsAnswered = payload;
    },
    setSessionClosed: (state, { payload }) => {
      state.isSessionClosed = payload;
    },
    addMessage: (state, { payload }) => {
      state.messages.push(payload);
    },
    setSessionId: (state, { payload }) => {
      state.sessionId = payload;
    },
  },
});

export const {
  startQuiz,
  setQuizMode,
  updateAnswer,
  removeAnswer,
  updatePercentage,
  setCurrentGroupIndex,
  markVideoAsSeen,
  nextGroup,
  prevGroup,
  resetQuiz,
  clearAnswers,
  setIntroVideoSeen,
  setAlertQuestionIds,
  setError,
  removeQuestionAlert,
  goBackToStart,
  setTermsAccepted,
  setSubmitting,
  setUploadingFile,
  setUploadedFileUrl,
  removeUploadedFile,
  setFileError,
  clearFileError,
  updateAnswerFile,
  setAllQuestionsAnswered,
  setSessionClosed,
  addMessage,
  setSessionId,
} = quizSlice.actions;
